/**
 * FZLCDropdownsHub — iter-255 (Feb 2026)
 * ======================================
 * Container hub for every dropdown-list catalog used to power company
 * management inside FZLC. Currently hosts the "قوائم خدمات الشركات
 * لملفات الشركات" card that was previously the ONLY card on the parent
 * FZLCManagementHub.
 *
 * Path: لوحة التحكم → التحكم والمعلومات للمنصات → CLOUDGM FZ LLC (للتحكم)
 *       → إدارة الشركة CLOUDGM FZ-LLC → القوائم المنسدلة لإدارة الشركة
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ListChecks, Package } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { useLanguage } from '../context/LanguageContext';
import { usePerms } from '../permissions/PermissionsContext';

export default function FZLCDropdownsHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const breadcrumb = [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
    { label_ar: 'إدارة الشركة CLOUDGM FZ-LLC', label_en: 'Manage CLOUDGM FZ-LLC', path: '/control/cloudgm-fzlc-management' },
    { label_ar: 'القوائم المنسدلة لإدارة الشركة', label_en: 'Company Management Dropdowns', path: null },
  ];

  const cards = [
    {
      id: 'company-services-catalog',
      title_ar: 'قوائم خدمات الشركات لملفات الشركات',
      title_en: 'Company Services Lists for Company Files',
      desc_ar: 'مسميات الخدمات (رئيسي/فرعي) اللي تظهر في زر "إدراج طلب خدمة" داخل ملف الشركة.',
      desc_en: 'Service names (main + sub) shown inside every company file.',
      icon: Package,
      color: '#34d399',
      path: '/control/cloudgm-fzlc-management/company-services',
      testid: 'fzlc-dropdowns-card-company-services',
    },
  ];

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader onBack={() => navigate('/control/cloudgm-fzlc-management')} />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={breadcrumb} />
        </div>
      </div>

      <main className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-3">
              <div className="h-11 w-11 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                <ListChecks className="h-5 w-5 text-purple-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white" data-testid="fzlc-dropdowns-title">
                {t('القوائم المنسدلة لإدارة الشركة', 'Company Management Dropdown Lists')}
              </h1>
            </div>
            <p className="text-[#808080] max-w-2xl mx-auto">
              {t(
                'أدر كل الكتالوجات والقوائم المنسدلة المستخدمة داخل ملفات الشركات ومنصاتها.',
                'Manage every dropdown catalog powering the company files and their sub-platforms.',
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {visiblePages(cards, 'path').map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.button
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  whileHover={{ y: -6, scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(c.path)}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center overflow-hidden transition-all hover:bg-white/[0.04] cursor-pointer"
                  data-testid={c.testid}
                >
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ boxShadow: `inset 0 0 0 1px ${c.color}60, 0 8px 32px ${c.color}20` }}
                  />
                  <div className="relative">
                    <div
                      className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center border transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}30` }}
                    >
                      <Icon className="h-10 w-10" style={{ color: c.color }} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-white">
                      {isAr ? c.title_ar : c.title_en}
                    </h3>
                    <p className="text-xs text-[#808080] px-2">
                      {isAr ? c.desc_ar : c.desc_en}
                    </p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
